import { AGREEMENT_PERMISSIONS, type AgreementPermission } from './agreementPermissions'
import { REQUEST_PERMISSIONS, type RequestPermission } from './requestPermissions'
import { SUPPLIER_PERMISSIONS, type SupplierPermission } from './supplierPermissions'
import type { FgaAssignableRelation } from './generated/model'

/**
 * The entities a permission set can govern, and the entities the per-record access API serves. A
 * set's entity is fixed at creation.
 */
export const PERMISSION_SET_ENTITIES = ['request', 'supplier', 'agreement'] as const
export type PermissionSetEntity = (typeof PERMISSION_SET_ENTITIES)[number]

/** The permission vocabulary of each entity, keyed by entity. */
export const PERMISSIONS_BY_ENTITY = {
  request: REQUEST_PERMISSIONS,
  supplier: SUPPLIER_PERMISSIONS,
  agreement: AGREEMENT_PERMISSIONS,
} as const satisfies Record<PermissionSetEntity, readonly string[]>

/**
 * How many changes the grant endpoints accept in one batch. Each change costs several sequential
 * OpenFGA round-trips to converge, so an unbounded batch would run into the lambda timeout mid-write
 * — a partial save — rather than being rejected cleanly.
 */
export const MAX_GRANT_CHANGES_PER_BATCH = 50

/**
 * The relations naming who takes part in a record, per entity — the model's `explicit_view` minus
 * the grant and supplier read-through arms, which are not held on the record itself.
 *
 * An agreement lists owners only: the supplier's contacts read through to it, but that access is
 * held on the supplier, so an access API cannot revoke it there.
 *
 * Named rather than derived, so a relation the model gains is a decision about whether it confers
 * revocable access. `modelWiring.test.ts` fails until that decision is made.
 */
export const PARTICIPANT_RELATIONS_BY_ENTITY = {
  request: ['derived_requester', 'derived_collaborator'],
  supplier: ['derived_contact'],
  agreement: ['derived_owner'],
} as const satisfies { [Entity in PermissionSetEntity]: readonly FgaAssignableRelation<Entity>[] }

/**
 * A permission of the named entity. Callers holding a runtime entity get the union; use the guard to
 * narrow.
 */
export type PermissionOf<Entity extends PermissionSetEntity> = (typeof PERMISSIONS_BY_ENTITY)[Entity][number]

export type PermissionSetPermission = RequestPermission | SupplierPermission | AgreementPermission

/**
 * Whether the permission belongs to the entity's vocabulary. The entities' permissions overlap in
 * shape but not in meaning, and one sent under the wrong entity is rejected by the permissions API, so
 * anything crossing that boundary has to be checked rather than assumed.
 */
export const isPermissionOfEntity = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permission: string
): permission is PermissionOf<Entity> => {
  const vocabulary: readonly string[] = PERMISSIONS_BY_ENTITY[entity]
  return vocabulary.includes(permission)
}

/** The entity's permissions, in vocabulary order, filtered to those present in `permissions`. */
export const permissionsOfEntity = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permissions: readonly string[]
): PermissionOf<Entity>[] => {
  const vocabulary: readonly PermissionOf<Entity>[] = PERMISSIONS_BY_ENTITY[entity]
  return vocabulary.filter((permission) => permissions.includes(permission))
}
